import { formatDate } from "@angular/common";
import { Observable, from, of, isObservable as checkIsObservable } from 'rxjs';
import { cloneDeep as loCloneDeep, groupBy as loGroupBy } from 'lodash-es';
import { isString, isNullOrEmpty } from './string-utils';

export type Required<T> = { [P in keyof T]-?: T[P] };

export interface Dictionary<TValue> {
  [key: string]: TValue;
}

export function isType<T>(value: any, predicate: (value: T) => boolean): value is T {
  return predicate(value);
}

export const neverReached = (never: never) => { };

/* tslint:disable */
/**
 * Determines if two objects or two values are equivalent.
 *
 * Two objects or values are considered equivalent if at least one of the following is true:
 *
 * * Both objects or values pass `===` comparison.
 * * Both objects or values are of the same type and all of their properties are equal by
 *   comparing them with `equals`.
 *
 * @param o1 Object or value to compare.
 * @param o2 Object or value to compare.
 * @returns true if arguments are equal.
 */
export function equals(o1: any, o2: any): boolean {
  if (o1 === o2) return true;
  if (o1 === null || o2 === null) return false;
  if (o1 !== o1 && o2 !== o2) return true; // NaN === NaN
  let t1 = typeof o1,
    t2 = typeof o2,
    length: number,
    key: any,
    keySet: any;
  if (t1 == t2 && t1 == 'object') {
    if (Array.isArray(o1)) {
      if (!Array.isArray(o2)) return false;
      if ((length = o1.length) == o2.length) {
        for (key = 0; key < length; key++) {
          if (!equals(o1[key], o2[key])) return false;
        }
        return true;
      }
    } else {
      if (Array.isArray(o2)) {
        return false;
      }
      keySet = Object.create(null);
      for (key in o1) {
        if (!equals(o1[key], o2[key])) {
          return false;
        }
        keySet[key] = true;
      }
      for (key in o2) {
        if (!(key in keySet) && typeof o2[key] !== 'undefined') {
          return false;
        }
      }
      return true;
    }
  }
  return false;
}
/* tslint:enable */

export function isDefined(value: any): boolean {
  return typeof value !== 'undefined' && value !== null;
}

export function isObject(item: any): boolean {
  return item && typeof item === 'object' && !Array.isArray(item);
}


export function mergeDeep(target: any, source: any): any {
  let output = Object.assign({}, target);
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach((key: any) => {
      if (isObject(source[key])) {
        if (!(key in target)) {
          Object.assign(output, { [key]: source[key] });
        } else {
          output[key] = mergeDeep(target[key], source[key]);
        }
      } else {
        Object.assign(output, { [key]: source[key] });
      }
    });
  }
  return output;
}

export function cloneArray(source: Array<any>): Array<any> {
  return source.map(p => { return { ...p } });
}

export function cloneDeep<T>(source: T): T {
  if (source == null) return null;
  return loCloneDeep(source);
}

/**
 * Determine if the argument is shaped like a Promise
 */
export function isPromise(obj: any): obj is Promise<any> {
  // allow any Promise/A+ compliant thenable.
  // It's up to the caller to ensure that obj.then conforms to the spec
  return !!obj && typeof obj.then === 'function';
}

/**
 * Deprecated use rxjs now.
 */
export function isObservable(obj: any | Observable<any>): obj is Observable<any> {
  return checkIsObservable(obj);

  // https://github.com/ReactiveX/rxjs/blob/master/CHANGELOG.md#610-2018-05-03
  //return !!obj && typeof obj.subscribe === 'function';
}

export function wrapIntoObservable<T>(value: T | Promise<T>| Observable<T>) {
  if (isObservable(value)) {
    return value;
  }

  if (isPromise(value)) {
    // Use `Promise.resolve()` to wrap promise-like instances.
    // Required ie when a Resolver returns a AngularJS `$q` promise to correctly trigger the
    // change detection.
    return from(Promise.resolve(value));
  }

  return of(value);
}

export function toDateString(value: string | number | Date): string {
  return formatDate(value, 'yyyy-MM-dd', 'en-US');
}

export function safeJsonParse(value: string): any {
  if (isNullOrEmpty(value)) return null;

  try {
    return JSON.parse(value);
  } catch (error) {
    console.error('Cannot convert Json', error);
    return null;
  }
}

export type Target = Window | Element;

// I attempt to scroll the given target to the given scrollTop and return the
// resultant value presented by the target.
export function scrollToTarget(target: Target, scrollTop: number): number {
  if ( target instanceof Window ) {
    target.scrollTo( 0, scrollTop );
    return( target.scrollY );
  } else if ( target instanceof Element ) {
    target.scrollTop = scrollTop;
    return( target.scrollTop );
  }
}

export function getValueByKey(value: any, key: string): any {
  if (value == null) return undefined;
  if (isString(value)) return value;

  let target = value;
  const keys = key.split('.');
  key = '';
  do {
    key += keys.shift();
    if (target != null && target[key] != null && (typeof target[key] === 'object' || !keys.length)) {
      target = target[key];
      key = '';
    } else if (!keys.length) {
      target = undefined;
    } else {
      key += '.';
    }
  } while (keys.length);

  return target;
}

export function TraverseObjectAndSearchForValue(target: any, key: string): any {
  let keys = key.split('.');
  let itemKey = '';
  do {
    itemKey += keys.shift();
    if (target != null && target[itemKey] != null && (typeof target[itemKey] === 'object' || !keys.length)) {
      target = target[itemKey];
      itemKey = '';
    } else {
      target = undefined;
      itemKey = '';
      break;
    }
  } while (keys.length);

  return { target, keys: keys.join('.') };
}

export function sanitizeData(data: any, keys: string[]): any {

  const sanitize = (obj: any): any => {
    for (let i in obj) {
      if (!obj.hasOwnProperty(i))
        continue;

      for (let key = 0; key < keys.length; key++) {
        let currentKey = keys[key];
        // look up key using index of key against the key value
        if (typeof obj[i] == 'object') {
          obj[i] = sanitize(obj[i]);
        } else if (i == currentKey) {
          delete obj[currentKey];
        }
      }
    }
    return obj;
  }

  return sanitize(cloneDeep(data));
}

export function safeNavigate<A, B>(op1: A, fn1: (a: A) => B): B;
export function safeNavigate<A, B, C>(op1: A, fn1: (a: A) => B, fn2: (b: B) => C): C;
export function safeNavigate<A, B, C, D>(op1: A, fn1: (a: A) => B, fn2: (b: B) => C, fn3: (c: C) => D): D;
export function safeNavigate<A, B, C, D, E>(op1: A, fn1: (a: A) => B, fn2: (b: B) => C, fn3: (c: C) => D, fn4: (d: D) => E): E;
export function safeNavigate<A, B, C, D, E, F>(op1: A, fn1: (a: A) => B, fn2: (b: B) => C, fn3: (c: C) => D, fn4: (d: D) => E, fn5: (e: E) => F): F;
export function safeNavigate<A, B, C, D, E, F, G>(op1: A, fn1: (a: A) => B, fn2: (b: B) => C, fn3: (c: C) => D, fn4: (d: D) => E, fn5: (e: E) => F, fn6: (f: F) => G): G;
/** 
 * Used to safely navigate object graphs. Equivalent of using 'elvis' operator. (This is redundant in typescript 3.7+) 
 * @method safeNavigate
*/
export function safeNavigate(value: any, ...fns: any[]): any {
  return fns.reduce((currentValue, fn) => currentValue === null || currentValue === undefined ? currentValue: fn(currentValue), value)
}


export function groupBy<T, TValue>(collection: Array<T>, fn: (item: T) => TValue): { [key: string]: T[] } {
  return loGroupBy(collection, fn);
}